/* <predictive-search>
   Wraps a <form> + <input data-q> + <div data-suggest-panel>.
   Debounces input, fetches /search/suggest?section_id=predictive-search&q=...,
   swaps the panel HTML. Closes on Escape, outside-click. Form submit is allowed
   to proceed natively (no preventDefault).

   Observed children:
     [data-q]             Input element.
     [data-suggest-panel] Empty <div> populated by fetched HTML.

   Dispatched events: none.
   Consumed events: none.

   Dependencies:
     window.LunchedRoutes.predictive_search_url (emitted from layout/theme.liquid).
*/
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this._debounceTimer = null;
    this._abortController = null;
    this._onInput = this._onInput.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onDocClick = this._onDocClick.bind(this);
  }

  connectedCallback() {
    this._input = this.querySelector('[data-q]');
    this._panel = this.querySelector('[data-suggest-panel]');
    if (!this._input || !this._panel) {
      console.error('[predictive-search]', 'missing-required-children');
      return;
    }
    this._input.addEventListener('input', this._onInput);
    this._input.addEventListener('focus', this._onFocus);
    this._input.addEventListener('blur', this._onBlur);
    this._input.addEventListener('keydown', this._onKeydown);
    document.addEventListener('click', this._onDocClick);
  }

  disconnectedCallback() {
    if (this._input) {
      this._input.removeEventListener('input', this._onInput);
      this._input.removeEventListener('focus', this._onFocus);
      this._input.removeEventListener('blur', this._onBlur);
      this._input.removeEventListener('keydown', this._onKeydown);
    }
    document.removeEventListener('click', this._onDocClick);
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    if (this._abortController) this._abortController.abort();
  }

  _onInput(e) {
    const q = e.target.value.trim();
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    if (q.length < 2) {
      this._closePanel();
      return;
    }
    this._debounceTimer = setTimeout(() => this._fetch(q), 150);
  }

  _onFocus() {
    const q = this._input.value.trim();
    if (q.length >= 2 && this._panel.innerHTML.trim() !== '') {
      this._openPanel();
    }
  }

  _onBlur() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this._closePanel();
    }, 150);
  }

  _onKeydown(e) {
    if (e.key === 'Escape') {
      this._closePanel();
      this._input.blur();
    }
  }

  _onDocClick(e) {
    if (!this.contains(e.target)) this._closePanel();
  }

  async _fetch(q) {
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();
    const base = (window.LunchedRoutes && window.LunchedRoutes.predictive_search_url) || '/search/suggest';
    const url = base + '?q=' + encodeURIComponent(q) + '&section_id=predictive-search&resources[type]=product&resources[limit]=5';
    try {
      const res = await fetch(url, { signal: this._abortController.signal });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const html = await res.text();
      this._panel.innerHTML = html;
      if (html.trim() === '') {
        this._closePanel();
      } else {
        this._openPanel();
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[predictive-search]', 'fetch-failed', err);
      this._closePanel();
    }
  }

  _openPanel() {
    this._panel.removeAttribute('hidden');
  }

  _closePanel() {
    this._panel.setAttribute('hidden', '');
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
